import { useCallback, useEffect, useRef, useState } from 'react'
import { createUser, getUserEmailUniqueness } from '@user/store/users/api'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { ITenantUserUpdate } from '@user/store/users/typings'
import { toast } from '@sales-center/components'
import {
  isAPIError,
  isAPIValidationError,
  validationErrorsToMap,
} from '@user/store/services/crud'
import { FormikHelpers } from 'formik'
import { getLicenseInfo } from '@user/store/licensing/api'

export const useCreateUser = (
  setHasFormErrors: React.Dispatch<React.SetStateAction<boolean>>,
  resetFormOnSuccess: (formikHelpers: FormikHelpers<any>) => void,
  onSuccess: () => void,
) => {
  const dispatch = useAppDispatch()
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )

  const [toSave, setToSave] = useState<ITenantUserUpdate>()
  const [formikHelpers, setFormikHelpers] = useState<FormikHelpers<any>>()

  const execute = useCallback(
    (user: ITenantUserUpdate, formikHelpers: FormikHelpers<any>) => {
      if (status != 'pending') {
        setToSave(user)
      }
      setFormikHelpers(formikHelpers)
    },
    [status],
  )

  const getUsers = async (toSave: ITenantUserUpdate) => {
    try {
      const result = await dispatch(getUserEmailUniqueness())

      if (getUserEmailUniqueness.fulfilled.match(result)) {
        setStatus('success')
        toast.success(
          toSave.externalUserId
            ? 'User created'
            : 'User created. Welcome email sent.',
        )
        dispatch(getLicenseInfo())
        if (formikHelpers) {
          resetFormOnSuccess(formikHelpers)
        }
        setHasFormErrors(false)
        onSuccessRef.current()
      } else {
        setStatus('fail')
        toast.error(`Error loading user list`)
      }
    } catch (error) {
      setStatus('fail')
    }
  }

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      if (toSave) {
        try {
          setStatus('pending')
          const result = await dispatch(createUser(toSave))

          if (mounted) {
            if (createUser.fulfilled.match(result)) {
              await getUsers(toSave)
            } else {
              if (isAPIError(result.payload)) {
                toast.error(result.payload.Message)
              } else if (isAPIValidationError(result.payload)) {
                const errors = validationErrorsToMap(result.payload)
                if (errors) {
                  toast.error([...errors.values()].flat().join(','))
                }
              } else {
                toast.error(`Unable to create user. ${result.error.message}`)
              }
              setHasFormErrors(true)
              setStatus('fail')
            }
          }
        } catch (error) {
          if (mounted) {
            setStatus('fail')
            toast.error(error.Message)
          }
        } finally {
          setToSave(undefined)
          setStatus('idle')
        }
      }
    }
    execute()

    return () => {
      mounted = false
    }
  }, [dispatch, formikHelpers, resetFormOnSuccess, setHasFormErrors, toSave])

  return { status, execute }
}
